// Invoices (Stage 2E-3): list (Open by default), editor for drafts, details for issued/cancelled invoices.
// Numbers, totals and every rule come from the database; issued invoices are never edited or deleted. Payment screens
// arrive in 2E-4 — the payment state shown here is derived by the database.
import { useState, useMemo, useEffect, useCallback } from "react";
import { DataTable, Select, Button, StatusBadge, Notice } from "../ui/kit/components";
import { text } from "../ui/kit/tokens";
import { loadInvoiceLines, errText } from "./api";
import { npr, dualDate, ktmToday, fyOf } from "./format.js";
import { invStatus, INV_STATUS, INV_OPEN, isOverdue } from "./invoiceDocs";
import InvoiceEditor from "./InvoiceEditor";
import InvoiceDetails from "./InvoiceDetails";

export default function Invoices({ data, reload, focusId, onFocused }) {
    const [filter, setFilter] = useState("open");
    const [editing, setEditing] = useState(null);            // null | initial draft for the editor
    const [openId, setOpenId] = useState(null);
    const [storeWarning, setStoreWarning] = useState("");
    const [err, setErr] = useState("");
    const today = ktmToday();
    const clientName = useMemo(() => Object.fromEntries(data.clients.map(c => [c.id, c.name])), [data.clients]);
    const docNo = useMemo(() => Object.fromEntries([...data.quotations, ...data.invoices].map(d => [d.id, d.doc_no])), [data.quotations, data.invoices]);
    const rows = useMemo(() => data.invoices.filter(v => {
        const s = invStatus(v);
        return filter === "all" || (filter === "open" ? INV_OPEN.includes(s) : filter === "paid" ? s === "paid" : s === "cancelled");
    }), [data.invoices, filter]);
    const fy = fyOf(today, data.calendar);
    const next = data.series.find(s => s.doc_type === "INV" && s.fy_label === fy);
    const nextInv = next ? `${data.settings?.invoice_prefix || "INV"}-${fy}-${String(next.next_no).padStart(4, "0")}` : null;
    const open = data.invoices.find(v => v.id === openId) || null;

    const show = useCallback(async (v) => {
        setErr(""); setStoreWarning("");
        if (v.status !== "draft") { setEditing(null); setOpenId(v.id); return; }
        try { setOpenId(null); setEditing({ ...v, lines: await loadInvoiceLines(v.id) }); } catch (e) { setErr(errText(e)); }
    }, []);
    // Open a given invoice (from a quotation's "Open invoice draft", or a newly created replacement) once it is loaded.
    const [pending, setPending] = useState(null);
    useEffect(() => {
        const id = pending || focusId;
        if (!id) return;
        const v = data.invoices.find(x => x.id === id);
        if (!v) return;
        show(v); setPending(null);
        if (id === focusId) onFocused?.();
    }, [pending, focusId, data.invoices, show, onFocused]);

    if (editing) return (
        <InvoiceEditor key={editing.id || "new"} initial={editing} data={{ ...data, nextInv }}
            onClose={async (changed) => { setEditing(null); if (changed) await reload(); }}
            onSaved={() => reload()}
            onIssued={async (v, warn) => { setEditing(null); await reload(); setStoreWarning(warn); setOpenId(v.id); }} />
    );
    return (
        <div>
            {err && <div style={{ marginBottom: 12 }}><Notice tone="danger">{err}</Notice></div>}
            {!fy && <div style={{ marginBottom: 12 }}><Notice tone="warning">Today's Nepali calendar month isn't verified, so invoices can't be issued yet (drafts can be saved).</Notice></div>}
            <DataTable rows={rows} defaultSort={{ key: "date", dir: "desc" }}
                searchText={v => [v.doc_no, clientName[v.client_id], v.subject, v.client_reference, docNo[v.quotation_id], docNo[v.replaces_invoice_id]].filter(Boolean).join(" ")}
                searchPlaceholder="Search number, client, subject, quotation…"
                empty={filter === "open" ? "No open invoices." : "No invoices here."}
                filters={<Select value={filter} onChange={e => setFilter(e.target.value)} style={{ width: 140 }} aria-label="Invoice filter">
                    <option value="open">Open</option><option value="paid">Paid</option><option value="cancelled">Cancelled</option><option value="all">All</option></Select>}
                toolbarRight={<Button onClick={() => { setOpenId(null); setEditing({}); }}>New invoice</Button>}
                onRowClick={show}
                columns={[
                    { key: "number", header: "Invoice", sortValue: v => v.doc_no || "~",
                        render: v => <div><div style={{ fontWeight: 600 }}>{v.doc_no || "Draft"}</div>
                            <div style={text.caption}>{clientName[v.client_id] || "—"}{v.quotation_id ? ` · from ${docNo[v.quotation_id]}` : ""}{v.replaces_invoice_id ? ` · replaces ${docNo[v.replaces_invoice_id]}` : ""}{v.subject ? ` · ${v.subject}` : ""}</div></div> },
                    { key: "date", header: "Date", sortValue: v => `${v.invoice_date}${v.created_at}`, render: v => dualDate(v.invoice_date, data.calendar), hideOnMobile: true },
                    { key: "due", header: "Due", sortValue: v => v.due_date, render: v => dualDate(v.due_date, data.calendar), hideOnMobile: true },
                    { key: "total", header: "Total (NPR)", sortValue: v => Number(v.total), render: v => <span style={text.money}>{npr(v.total)}</span> },
                    { key: "status", header: "Status", sortValue: v => invStatus(v), render: v => (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <StatusBadge status={INV_STATUS[invStatus(v)][1]} label={INV_STATUS[invStatus(v)][0]} />
                            {isOverdue(v) && <StatusBadge status="overdue" label="Overdue" />}
                        </div>) },
                ]} />
            {open && <InvoiceDetails v={open} data={data} storeWarning={storeWarning} onClose={() => setOpenId(null)} onChanged={reload}
                onOpenInvoice={async (id) => { setOpenId(null); await reload(); setPending(id); }} />}
        </div>
    );
}
